import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Group, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { CustomerDto } from './types';
import { useCreateCustomer, useUpdateCustomer } from './customersApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  customer: CustomerDto | null;
}

interface FormValues {
  phone: string;
  fullName: string;
  email: string;
  isActive: boolean;
  loyaltyCardId: string;
}

export function CustomerFormDrawer({ opened, onClose, customer }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const submitting = create.isPending || update.isPending;
  const editing = customer !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { phone: '', fullName: '', email: '', isActive: true, loyaltyCardId: '' },
    validate: {
      phone: (v) => (v.trim() ? null : t('common.errors.required')),
      fullName: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      phone: customer?.phone ?? '',
      fullName: customer?.fullName ?? '',
      email: customer?.email ?? '',
      isActive: customer?.isActive ?? true,
      loyaltyCardId: customer?.loyaltyCardId ?? '',
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, customer]);

  const submit = async (values: FormValues) => {
    const payload = {
      phone: values.phone.trim(),
      fullName: values.fullName.trim(),
      email: values.email.trim() || null,
      loyaltyCardId: values.loyaltyCardId.trim() || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: customer.customerId,
          request: { ...payload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.fullName,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('customers.form.editTitle') : t('customers.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('customers.form.phoneLabel')}
            placeholder="0599123456"
            withAsterisk
            className="font-numeric"
            dir="ltr"
            {...form.getInputProps('phone')}
          />
          <TextInput
            label={t('customers.form.fullNameLabel')}
            placeholder={t('customers.form.fullNamePlaceholder')}
            withAsterisk
            {...form.getInputProps('fullName')}
          />
          <TextInput
            label={t('customers.form.emailLabel')}
            placeholder="customer@example.com"
            dir="ltr"
            {...form.getInputProps('email')}
          />
          <TextInput
            label={t('customers.form.loyaltyCardLabel')}
            description={t('customers.form.loyaltyCardHelp')}
            placeholder={t('customers.form.loyaltyCardPlaceholder')}
            dir="ltr"
            className="font-numeric"
            {...form.getInputProps('loyaltyCardId')}
          />
          {editing && (
            <Switch
              label={t('customers.form.activeLabel')}
              description={t('customers.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
